import { ZuruflexApiError } from '@zuruflex/api-client';
import {
  HANDOVER_ITEMS_MAX,
  HANDOVER_ITEM_MAX_LENGTH,
  handoverItemKey,
  type Vehicle,
} from '@zuruflex/types';
import { Alert, Button, Input } from '@zuruflex/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';

/**
 * What goes out with this car: the second key, the child seat, the
 * charging cable.
 *
 * The list is the owner's own wording, and it is copied onto every
 * pick-up record at the moment of the handover — ticked off as it goes
 * out, and checked back in at the return against exactly what left. So
 * editing it here changes the next hire's checklist and never restates a
 * past one, and it does not send a live car back for review: nothing on it
 * is something a renter is shown before they book.
 *
 * Its own small form rather than more fields on `CarDetailsForm`, because
 * that form's save re-prices and re-reviews the car. A partner adding a
 * phone holder should not have to wonder whether that took the listing
 * down.
 */
export function HandoverItemsEditor({ vehicle }: { vehicle: Vehicle }) {
  const { t } = useTranslation(['partner', 'common']);
  const queryClient = useQueryClient();

  const [items, setItems] = useState<string[]>(vehicle.handoverItems);
  /** What the server last said, so Save only wakes up for a real change. */
  const [savedItems, setSavedItems] = useState<string[]>(vehicle.handoverItems);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const dirty = items.join('\n') !== savedItems.join('\n');
  const full = items.length >= HANDOVER_ITEMS_MAX;

  const save = useMutation({
    mutationFn: () => api.updateVehicle(vehicle.id, { handoverItems: items }),
    onMutate: () => {
      setError(null);
      setSaved(false);
    },
    onError: (cause) =>
      setError(cause instanceof ZuruflexApiError ? cause.message : t('common:error.unexpected')),
    onSuccess: async (updated) => {
      // The server trims and de-duplicates, so its answer is the list —
      // not the one that was sent.
      setItems(updated.handoverItems);
      setSavedItems(updated.handoverItems);
      setSaved(true);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['vehicle', vehicle.id] }),
        queryClient.invalidateQueries({ queryKey: ['my-fleet'] }),
      ]);
    },
  });

  /*
   * Checked here against the same key the API and the return check match
   * on, so "2 keys" and "2 Keys " are one item before they are two rows a
   * renter is asked about separately.
   */
  const add = (event: FormEvent) => {
    event.preventDefault();
    const label = draft.trim();
    if (!label || full) return;
    if (items.some((item) => handoverItemKey(item) === handoverItemKey(label))) {
      setError(t('carDetail.items.duplicate', { item: label }));
      return;
    }
    setError(null);
    setSaved(false);
    setItems([...items, label]);
    setDraft('');
  };

  const remove = (index: number) => {
    setSaved(false);
    setItems(items.filter((_, at) => at !== index));
  };

  /** Order is the order they are ticked off in at the car. */
  const move = (index: number, by: -1 | 1) => {
    const to = index + by;
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    [next[index], next[to]] = [next[to] as string, next[index] as string];
    setSaved(false);
    setItems(next);
  };

  return (
    <div className="flex flex-col gap-4">
      {error ? <Alert variant="danger">{error}</Alert> : null}
      {saved && !dirty ? <Alert variant="success">{t('carDetail.saved')}</Alert> : null}

      {items.length > 0 ? (
        <ol className="divide-y divide-border-default rounded-field border border-border-default">
          {items.map((item, index) => (
            <li key={handoverItemKey(item)} className="flex items-center gap-2 py-1.5 pl-3 pr-1.5">
              <span className="w-5 shrink-0 text-xs tabular-nums text-text-muted">{index + 1}</span>
              <span className="min-w-0 flex-1 truncate text-sm">{item}</span>
              <Button
                type="button"
                size="icon-sm"
                variant="ghost"
                disabled={index === 0 || save.isPending}
                aria-label={t('carDetail.items.moveUp', { item })}
                onClick={() => move(index, -1)}
              >
                <ArrowUp className="size-4" />
              </Button>
              <Button
                type="button"
                size="icon-sm"
                variant="ghost"
                disabled={index === items.length - 1 || save.isPending}
                aria-label={t('carDetail.items.moveDown', { item })}
                onClick={() => move(index, 1)}
              >
                <ArrowDown className="size-4" />
              </Button>
              <Button
                type="button"
                size="icon-sm"
                variant="ghost"
                disabled={save.isPending}
                aria-label={t('carDetail.items.remove', { item })}
                onClick={() => remove(index)}
              >
                <X className="size-4" />
              </Button>
            </li>
          ))}
        </ol>
      ) : (
        <p className="rounded-field border border-dashed border-border-strong px-4 py-5 text-center text-sm text-text-muted">
          {t('carDetail.items.empty')}
        </p>
      )}

      {/* Enter adds, the way a list of short things is typed. */}
      <form className="flex gap-2" onSubmit={add}>
        <Input
          aria-label={t('carDetail.items.newItem')}
          value={draft}
          maxLength={HANDOVER_ITEM_MAX_LENGTH}
          disabled={full || save.isPending}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={t('carDetail.items.placeholder')}
          className="flex-1"
        />
        <Button type="submit" variant="outline" disabled={!draft.trim() || full || save.isPending}>
          <Plus className="size-4" />
          {t('common:action.add')}
        </Button>
      </form>

      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-text-muted">
          {full
            ? t('carDetail.items.full', { max: HANDOVER_ITEMS_MAX })
            : t('carDetail.items.limit', { used: items.length, max: HANDOVER_ITEMS_MAX })}
        </p>
        <Button
          type="button"
          disabled={!dirty}
          isLoading={save.isPending}
          onClick={() => save.mutate()}
        >
          {t('common:action.save')}
        </Button>
      </div>
    </div>
  );
}
